/*
 *  CNNIX IO
 *  @description:   A simple library for managing data transfer.
 *  @author:        Kenneth Uzquiano (kenneth.uzquiano@turner.com)
 *  @version:       0.0.1
 *
 *  CNNIX IO allows developers to use one small library to access XHR, Fetch, and
 *  JSONp methods to load/send data to/from a server.
 *
 *  Use `import` or `require` to pull in this file as it's own object. All functions
 *  are constants so there is no need to create a new instance.
 *
 *    - IO.Get(url, options, xhr) - Invoke a request using a `get` method.
 *    - IO.Post(url, options, xhr) - Invoke a request using a `get` method.
 *    - IO.Delete(url, options, xhr) - Invoke a request using a `get` method.
 *        - `url` [String] - The request URL
 *        - `options` [Object] - An options object. This object differes between XHR and Fetch requests
 *          - XHR options can include a `success` and `error` function, as well as a
 *            a `data` or `body` object for transmitting data.
 *          - Fetch options mirror the standard `fetch` options.
 *        - xhr [Boolean] - Set to `true` to use an XHR request.
 *
 *  You may also use the `constructRequest` function to build your own request, or
 *  `xhr` or `request` functions to target a XHR or Fetch request directly. (Because
 *  Fetch is the living standard, it is given the name `request` for when it becomes
 *  the standard.)
 *
 *  JSONp requests are also supported using the `jsonp` function.
 *
 *    - IO.jsonp(url, callback, errorCallback, {callbackName}) - JSONp request
 *        - `url` [String] - The request URL
 *        - `callback` [Function] - Callback to recieve data after load.
 *        - `errorCallback` [Function] - Callback to recieve error on error.
 *        - `callbackName` [String] - The name of the jsonCallback that is expected on import.
 *          - Most of the time this can be set dynamically, however, if it is known that
 *            a certain callback ID is required, you can use this value to set it.
 *
 */

    const Method =
    {
        GET :       "get",
        POST :      "post",
        DELETE :    "delete",
    }

    const constructRequest = (url, method, options, useXHR) => (useXHR || options === true)
        ? xhr(url, method, options)
        : request(url, method, options);

    export const Get = (url, options={}, xhr=false) => constructRequest(url, Method.GET, options, xhr);
    export const Post = (url, options={}, xhr=false) => constructRequest(url, Method.POST, options, xhr);
    export const Delete = (url, options={}, xhr=false) => constructRequest(url, Method.DELETE, options, xhr);

    export const xhr = (url, method=Method.GET, options={}) =>
    {
        if (window.XMLHttpRequest)
        {
            let payload = options.body ? options.body : options.data ? JSON.stringify(options.data) : null,
                xhr = new XMLHttpRequest();
                xhr.onerror = options.error;
                xhr.onload = options.success;
                xhr.open(method, url, true);

                if( payload ) xhr.setRequestHeader("Content-Type", "application/json;charset=UTF-8");

                xhr.send(payload);
        }
    }

    export const request = (url, method=Method.GET, options={}) =>
        fetch(
            (url + (options.query != undefined ? ("?" + options.query) : "")),
            {
                body : options.body ? options.body : options.data ? JSON.stringify(options.data) : undefined,
                headers : options.headers !== undefined ? options.headers != false ? options.headers : undefined : { 'content-type': 'application/json' },
                multipart : options.multipart != undefined ? options.multipart : undefined,
                credentials: options.credentials != undefined ? options.credentials : 'same-origin',
                referrer: options.referrer != undefined ? options.referrer : 'no-referrer',
                cache: options.cache != undefined ? options.cache : 'no-cache',
                mode: options.mode != undefined ? options.mode : 'cors',
                method,
            }
        );

    export const jsonp = (url, callback, errorCallback, callbackName='jsonpCallback') =>
    {
        if(url != undefined)
        {
            let script,
                callbackName = callbackName || 'jsonpCallback';

            window[callbackName] = function(data)
            {
                delete window[callbackName];
                document.body.removeChild(script);
                if(callback) callback(data);
            };

            script = document.createElement('script');
            script.src = url + ((url.indexOf('?') >= 0 ? '&' : '?') + 'callback=' + callbackName) + ("&cb=" + Math.floor(Math.random() * 100000000000));
            if(errorCallback) script.onerror = errorCallback;
            document.body.appendChild(script);
        }
    }
